import {
  buildEndpoint,
  buildQueryUrl,
  ColumnLayout,
  DefaultInputLabel,
  DropdownItemModel,
  ErrorDisplay,
  FormActionTypes,
  getFieldValue,
  getPropertyByString,
  getValueFromChangeEvent,
  InputType,
  METHODS,
  MultiModalProvider,
  Render,
  SmartInputBlock,
  toggledClass,
  useApiWorker,
  useFormState,
  useFormStateValue,
  useFormUpdater,
  useModalHelpers,
  useToasts,
} from "@techsharegov/react-ui";
import Dynamsoft from "dwt";
import { WebTwain } from "dwt/dist/types/WebTwain";
import {
  FilePondFile,
  ProcessServerConfigFunction,
  RevertServerConfigFunction,
} from "filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import "filepond/dist/filepond.min.css";
import { AgendaEndpoints } from "Models/Routes";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { FilePond, FilePondProps, registerPlugin } from "react-filepond";
import { Col, Row } from "reactstrap";
import { v4 as uuidv4 } from "uuid";

const scanModalId = "fileUploadScanDocumentsModal";

const oneMB = 1048576;
const maxSize = oneMB * 4;

export type RenderAdditionalFormComponentsProp = (
  handleFileFieldChange: (e: any, type: InputType, fieldName: string) => void,
  formatFieldName: (name: string) => string
) => React.ReactElement;

export type SmartFileUploaderProps = FilePondProps & {
  fileEndpoint: string;
  name?: string;
  requireAttachmentType?: boolean;
  vertical?: boolean;
  isEdit?: boolean;
  withComments?: boolean;
  label?: string;
  isAdministrativeAttachmentType?: boolean;
  renderAdditionalFormComponents?: RenderAdditionalFormComponentsProp;
  allowScanning?: boolean;
  defaultAttachmentType?: DropdownItemModel;
};

export const SmartFileUploader: React.FC<SmartFileUploaderProps> = (props) => {
  return (
    <MultiModalProvider>
      <SmartFileUploaderContent {...props} />
    </MultiModalProvider>
  );
};

export const SmartFileUploaderContent: React.FC<SmartFileUploaderProps> = ({
  fileEndpoint,
  name = "files",
  label,
  required,
  requireAttachmentType,
  allowMultiple,
  vertical,
  isEdit,
  withComments,
  isAdministrativeAttachmentType,
  renderAdditionalFormComponents,
  allowScanning = true,
  acceptedFileTypes = [],
  defaultAttachmentType,
  ...filePondProps
}) => {
  const { formValues, formErrors } = useFormState();
  const sendUpdate = useFormUpdater();
  const API = useApiWorker();
  var filePondRef = useRef<FilePond | null>(null);
  const toast = useToasts();
  const [hasFiles, setHasFiles] = useState<boolean>(isEdit ?? false);
  const [canAddSingle, setCanAddSingle] = useState<boolean>(true);
  const [fileErrors, setFileErrors] = useState<string[]>([]);
  const { toggle: toggleScanModal } = useModalHelpers(scanModalId);
  const attachmentTypeFieldName = "attachmentType";
  const commentsFieldname = "description";
  const acceptsPdf =
    !acceptedFileTypes.length ||
    acceptedFileTypes.some(
      (t) => t === "application/pdf" || t === "application/*"
    );
  const isAbleToScan =
    allowScanning && !isEdit && acceptsPdf && (allowMultiple || canAddSingle);
  const uploadUrl = buildEndpoint(fileEndpoint, METHODS.Upload);
  const deleteUrl = buildEndpoint(fileEndpoint, METHODS.DeleteTempBlob);

  const deleteUrlWithId = (id: string) => {
    return buildQueryUrl(deleteUrl, { id: id });
  };

  const formatFieldName = (fieldName: string) => {
    return allowMultiple ? `${name}[0].${fieldName}` : fieldName;
  };

  registerPlugin(FilePondPluginFileValidateType);

  useEffect(() => {
    var hasValues =
      (allowMultiple && getPropertyByString(formValues, name)?.length > 0) ||
      (!allowMultiple && formValues?.fileId?.length > 0);

    if (hasFiles !== hasValues) {
      setHasFiles(hasValues);
    }
  }, [formValues]);

  useEffect(() => {
    const fileErrorValues = allowMultiple
      ? formErrors[formatFieldName("fileid")]
      : formErrors["fileid"];

    setFileErrors(fileErrorValues);
  }, [formErrors]);

  const [attachmentType, setAttachmentType] =
    useFormStateValue<DropdownItemModel>(
      formatFieldName(attachmentTypeFieldName)
    );
  useEffect(() => {
    if (filePondRef && !hasFiles) {
      filePondRef.current?.removeFiles();
    }

    if (defaultAttachmentType !== undefined && hasFiles) {
      setAttachmentType(defaultAttachmentType);
    }
  }, [hasFiles]);

  const formatFile = ({ filename, fileSize, serverId }: FilePondFile) => {
    return {
      fileId: serverId,
      fileName: filename,
      size: fileSize,
    };
  };

  const getEmptyFileFields = () => {
    return {
      ...formValues,
      fileId: undefined,
      fileName: undefined,
      size: undefined,
      description: undefined,
      attachmentType: undefined,
    };
  };

  const clearFileFields = () => {
    var clearedValues = getEmptyFileFields();

    sendUpdate(FormActionTypes.SetValues, {
      field: name,
      value: clearedValues,
    });
  };

  /* Handles chunking + posting temp file in chunks */
  const handleProcess: ProcessServerConfigFunction = async (
    fieldName,
    file,
    metadata,
    load,
    error,
    progress,
    abort
  ) => {
    var aborted = false;
    const handleAbort = () => {
      aborted = true;
      abort();
    };

    if (maxSize !== undefined && file.size > maxSize) {
      toast.error(
        "File is too large. Please make sure your upload is less than " +
          maxSize
      );
      return handleAbort();
    }

    const CHUNK_SIZE =
      1024 *
      1024 *
      (parseInt(
        process.env.NODE_ENV === "development"
          ? `${process.env.REACT_APP_FILE_UPLOAD_CHUNK_SIZE}`
          : `${window.FILE_UPLOAD_CHUNK_SIZE}`
      ) ?? 1);
    var chunk = 0;
    const fileId = uuidv4();
    var numChunks = Math.ceil(file.size / CHUNK_SIZE);

    while (chunk * CHUNK_SIZE < file.size) {
      var blob = file.slice(CHUNK_SIZE * chunk, CHUNK_SIZE * (chunk + 1));
      const formData = new FormData();
      formData.append("contentType", file.type);
      formData.append("fileName", file.name);
      formData.append("chunk", `${chunk}`);
      formData.append("chunks", `${numChunks}`);
      formData.append("id", fileId);
      formData.append("file", blob);
      const response = await API.post(uploadUrl, formData);
      chunk++;
      progress(true, chunk, numChunks);
    }

    load(fileId);
    return {
      abort: handleAbort,
    };
  };

  /* Handles deleting temp file */
  const handleRevert: RevertServerConfigFunction = async (
    uniqueFileId,
    load,
    error
  ) => {
    try {
      await API.post(deleteUrlWithId(uniqueFileId));
    } catch (err: any) {
      error(err);
    }

    if (allowMultiple) {
      const filteredFiles = (getFieldValue(formValues, name) || []).filter(
        (file: any) => file.fileId !== uniqueFileId
      );

      sendUpdate(FormActionTypes.UpdateValue, {
        field: name,
        value: filteredFiles,
      });
    } else {
      clearFileFields();
    }

    load();
  };

  const handleProcessedFiles = () => {
    const files = filePondRef.current?.getFiles();

    if (allowMultiple) {
      sendUpdate(FormActionTypes.UpdateValue, {
        field: name,
        value: files?.map((file) => formatFile(file)),
      });
    } else {
      var fileValue = files ? formatFile(files[0]) : getEmptyFileFields();
      sendUpdate(FormActionTypes.SetValues, {
        ...formValues,
        ...fileValue,
      });
    }
  };

  const handleFileFieldChange = (
    e: any,
    type: InputType,
    fieldName: string
  ) => {
    const value = getValueFromChangeEvent(e, type);
    const fileList = getFieldValue(formValues, name);
    const newFileList = [
      ...(fileList || []).map((file: any) => {
        return { ...file, [fieldName]: value };
      }),
    ];

    sendUpdate(FormActionTypes.UpdateValue, {
      field: name,
      value: newFileList,
    });
  };

  const updateCanAddSingle = () => {
    setCanAddSingle(!filePondRef.current?.getFiles().length);
  };

  const handleScannedDocumentUploaded = async (
    fileName: string,
    fileBlob: Blob
  ) => {
    try {
      const name = fileName.toLowerCase().endsWith(".pdf")
        ? fileName
        : fileName + ".pdf";

      if (!allowMultiple) {
        filePondRef.current?.removeFiles();
      }
      await filePondRef.current?.addFile(new File([fileBlob], name));
      toggleScanModal();
    } catch (e) {
      toast.error("An error occurred uploading the scanned document.");
    }
  };

  const uploadScannedDocument = (
    fileName: string,
    pages: number,
    dwObject: WebTwain
  ) => {
    dwObject.ConvertToBlob(
      Array.from(Array(pages).keys()),
      Dynamsoft.DWT.EnumDWT_ImageType.IT_PDF,
      (blob) => handleScannedDocumentUploaded(fileName, blob),
      () => toast.error("An error occurred uploading the scanned document.")
    );
  };

  const server = {
    process: handleProcess,
    revert: handleRevert,
  };

  return (
    <Fragment>
      {/* <MultiModalDialog modalId={scanModalId} title="Scan Documents">
        <FormProvider>
          <DwtScannerProvider>
            <ScannerModalContent uploadFile={uploadScannedDocument} />
          </DwtScannerProvider>
        </FormProvider>
      </MultiModalDialog> */}
      <Row>
        <Render condition={!isEdit}>
          <Col xs={12} className="transition-all">
            <DefaultInputLabel label={label} required={required} />
            <div
              className={toggledClass("with-errors", fileErrors?.length > 0)}
            >
              <FilePond
                name={name}
                server={server}
                onprocessfiles={handleProcessedFiles}
                onaddfile={updateCanAddSingle}
                onremovefile={updateCanAddSingle}
                ref={(ref) => (filePondRef.current = ref)}
                allowMultiple={allowMultiple}
                acceptedFileTypes={acceptedFileTypes}
                {...filePondProps}
              />
            </div>
            <ErrorDisplay errors={fileErrors} />
          </Col>
        </Render>

        {/* <Render condition={isAbleToScan}>
          <Col className="flex-center mb-large">
            <TextButton noBold onClick={toggleScanModal}>
              Scan document instead
              <ArrowForwardIcon
                style={{ height: 16, width: 16 }}
                className="ml-tiny"
              />
            </TextButton>
          </Col>
        </Render> */}
        <Render condition={hasFiles}>
          <Col xs={12} className="transition-all animate-fade-in">
            <Render condition={requireAttachmentType}>
              <SmartInputBlock
                name={formatFieldName(attachmentTypeFieldName)}
                otherErrorNames={[
                  `${attachmentTypeFieldName}.Id`,
                  `${attachmentTypeFieldName}.Name`,
                ]}
                onChangeOverride={
                  allowMultiple
                    ? (e: any) =>
                        handleFileFieldChange(
                          e,
                          InputType.DropDown,
                          attachmentTypeFieldName
                        )
                    : undefined
                }
                label="Attachment Type"
                type={InputType.DropDown}
                readEndpoint={
                  AgendaEndpoints.actionitemattachments.dropdownItems
                }
                required
                noSidePadding
                useDiv
                value={defaultAttachmentType}
                width="100%"
              />
            </Render>
            <Render condition={withComments}>
              <SmartInputBlock
                name={formatFieldName(commentsFieldname)}
                onChangeOverride={
                  allowMultiple
                    ? (e: any) =>
                        handleFileFieldChange(
                          e,
                          InputType.TextArea,
                          commentsFieldname
                        )
                    : undefined
                }
                label="Description"
                placeholder="Enter description here..."
                type={InputType.TextArea}
                colProps={ColumnLayout.Full}
                noSidePadding
              />
            </Render>
          </Col>
          {renderAdditionalFormComponents &&
            renderAdditionalFormComponents(
              handleFileFieldChange,
              formatFieldName
            )}
        </Render>
      </Row>
    </Fragment>
  );
};

export interface ScannerModalContentProps {
  uploadFile: (fileName: string, pages: number, dwObject: WebTwain) => void;
}

// const ScannerModalContent: React.FC<ScannerModalContentProps> = ({
//   uploadFile,
// }) => {
//   const toast = useToasts();
//   const { setFormErrors, clearFormErrors } = useFormHelpers();
//   const { fileName, pageCount, dwObject } = useDwtScannerState();

//   const submitAction = () => {
//     clearFormErrors();

//     if (!fileName.trim().length) {
//       toast.error('Please set a file name.');
//       setFormErrors({ filename: ["'File Name' must not be empty."] });
//       return;
//     }

//     if (!pageCount) {
//       toast.error('No scanned document found.');
//       return;
//     }

//     if (dwObject.current === null) {
//       toast.error('An error occurred uploading the scanned document.');
//       return;
//     }

//     uploadFile(fileName, pageCount, dwObject.current);
//   };

//   return (
//     <Fragment>
//       <DwtScanner noProvider />
//       <DialogActionsBar>
//         <div>
//           <MultiModalCancelButton modalId={scanModalId} />
//           <PrimaryButton onClick={submitAction}>Upload</PrimaryButton>
//         </div>
//       </DialogActionsBar>
//     </Fragment>
//   );
// };
